import {
  APP_INITIALIZER,
  ApplicationConfig,
  Injector,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { BarController, BarElement, CategoryScale, LinearScale, Tooltip } from 'chart.js';
import { provideCharts } from 'ng2-charts';
import { routes } from './app.routes';
import { PERMISSIONS } from './core/auth/permissions.const';
import { provideAppConfig } from './core/config/providers';
import { handleMsalRedirect } from './core/auth/handle-msal-redirect';
import { provideCoreAuth } from './core/auth/provide-auth';
import { provideCoreHttp } from './core/http/provide-http';
import { runtimeConfig } from './core/config/runtime-config';
import { AppConfig } from './core/config/app-config.token';
import { EntitlementsHttpRepository } from './core/entitlements/entitlements.http';
import { EntitlementsRepository } from './core/entitlements/entitlements.repository';
import { NAV_SECTIONS } from './shared/nav/nav-sections.token';
import { NavSection } from './shared/nav/nav-section.model';

async function loadRuntimeConfig(): Promise<void> {
  try {
    const res = await fetch('/config.json');
    if (!res.ok) return;
    const json: Partial<AppConfig> = await res.json();
    Object.assign(runtimeConfig, json, {
      auth: { ...runtimeConfig.auth, ...json.auth },
      http: { ...runtimeConfig.http, ...json.http },
      staff: { ...runtimeConfig.staff, ...json.staff },
    });
  } catch {
    return; // config.json ausente o red caída: se usan los valores compilados
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: (injector: Injector) => async () => {
        await loadRuntimeConfig();
        const msal = injector.get(MsalService);
        const broadcast = injector.get(MsalBroadcastService);
        await msal.instance.initialize();
        await handleMsalRedirect(msal);
        broadcast.resetInProgressEvent();
      },
      deps: [Injector],
      multi: true,
    },
    provideZonelessChangeDetection(),
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
    ),
    ...provideAppConfig(),
    ...provideCoreHttp(runtimeConfig.apiBaseUrl, []),
    ...provideCoreAuth(runtimeConfig.auth.msal),
    provideCharts({
      registerables: [BarController, BarElement, CategoryScale, LinearScale, Tooltip],
    }),
    { provide: EntitlementsRepository, useClass: EntitlementsHttpRepository },
    {
      provide: NAV_SECTIONS,
      useValue: {
        title: 'Beneficiarios',
        items: [
          {
            label: 'Padrón de Beneficiarios',
            route: '/beneficiarios/padron-beneficiarios',
            permission: PERMISSIONS.BENEFICIARIOS_PADRON_READ,
          },
          {
            label: 'Consulta Unitaria',
            route: '/beneficiarios/consulta-unitaria',
            permission: PERMISSIONS.BENEFICIARIOS_CONSULTA_READ,
          },
          {
            label: 'Consulta por RUT Titular',
            route: '/beneficiarios/consulta-rut-titular',
            permission: PERMISSIONS.BENEFICIARIOS_TITULAR_READ,
          },
          {
            label: 'Reportería Agregada',
            route: '/beneficiarios/reporteria-agregada',
            permission: PERMISSIONS.BENEFICIARIOS_REPORTERIA_READ,
          },
          {
            label: 'Historial de Interacción',
            route: '/beneficiarios/historial-interaccion',
            permission: PERMISSIONS.BENEFICIARIOS_HISTORIAL_READ,
          },
        ],
      } satisfies NavSection,
      multi: true,
    },
    {
      provide: NAV_SECTIONS,
      useValue: {
        title: 'Staff en Convenio',
        items: [
          {
            label: 'Listado de Staff',
            route: '/staff-convenio/listado-staff',
            permission: PERMISSIONS.STAFF_VIGENTE_READ,
          },
          {
            label: 'Alta de Staff',
            route: '/staff-convenio/alta-staff',
            permission: PERMISSIONS.STAFF_ALTA_CREATE,
          },
          {
            label: 'Baja de Staff',
            route: '/staff-convenio/baja-staff',
            permission: PERMISSIONS.STAFF_BAJA_DELETE,
          },
          {
            label: 'Historial de Cambios',
            route: '/staff-convenio/historial-cambios',
            permission: PERMISSIONS.STAFF_HISTORIAL_READ,
          },
        ],
      } satisfies NavSection,
      multi: true,
    },
    {
      provide: NAV_SECTIONS,
      useValue: {
        title: 'Gestión del Prestador',
        items: [
          {
            label: 'Antecedentes del Prestador',
            route: '/gestion-prestador/antecedentes-prestador',
            permission: PERMISSIONS.PRESTADOR_ANTECEDENTES_READ,
          },
          {
            label: 'Fechas de Pago',
            route: '/gestion-prestador/fechas-pago',
            permission: PERMISSIONS.PRESTADOR_PAGOS_READ,
          },
          {
            label: 'Cuentas Hospitalarias',
            route: '/gestion-prestador/cuentas-hospitalarias',
            permission: PERMISSIONS.PRESTADOR_CUENTAS_READ,
          },
          {
            label: 'Emisión de Bonos PAM',
            children: [
              {
                label: 'Emitir Bono',
                route: '/gestion-prestador/emision-bonos/emitir-bono',
                permission: PERMISSIONS.PRESTADOR_BONOS_CREATE,
              },
              {
                label: 'Nómina',
                route: '/gestion-prestador/emision-bonos/nomina',
                permission: PERMISSIONS.PRESTADOR_NOMINAS_READ,
              },
            ],
            expandable: true,
          },
          {
            label: 'Cobros de Bonos Prestador',
            children: [
              {
                label: 'Cargar Bonos',
                route: '/gestion-prestador/cobros-bonos/cargar-bonos',
                permission: PERMISSIONS.PRESTADOR_CARGAS_CREATE,
              },
              {
                label: 'Liquidaciones Cargadas',
                route: '/gestion-prestador/cobros-bonos/liquidaciones',
                permission: PERMISSIONS.PRESTADOR_LIQUIDACIONES_READ,
              },
            ],
            expandable: true,
          },
        ],
      } satisfies NavSection,
      multi: true,
    },
  ],
};
